import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useApp } from '../../state/AppState';
import { useI18n, type I18nKey } from '../../i18n';
import { AstrallyMark } from '../../components/AstrallyMark';

/**
 * Developer unlock: only rendered when VITE_DEV_UNLOCK=true, and only
 * *granted* for allowlisted accounts (checked server-side), so the two locks
 * are independent.
 */
const DEV_UNLOCK_BUILD = import.meta.env.VITE_DEV_UNLOCK === 'true';

/**
 * Billing errors are the ones users are least willing to puzzle over — and
 * the right message differs by platform. Telling someone standing *in* the
 * Android app to "purchase in the Android app" is the confusing case.
 */
function friendlyBillingError(raw: string, isNative: boolean, t: (key: I18nKey) => string): string {
  if (raw.includes('dev-unlock-not-allowed')) {
    // The remedy (add the email to devUnlockEmails in config/app) is an
    // operator action on a dev build, so it goes to the console — a build
    // without the flag must never leak our Firestore layout to a customer.
    if (DEV_UNLOCK_BUILD) {
      console.warn(
        '[unlock] Developer unlock refused: add this account’s email to devUnlockEmails in the Firestore config/app document.',
      );
    }
    return 'This account is not on the developer allowlist.';
  }
  if (raw.includes('play-billing-pending') || raw.includes('purchase-in-app-only')) {
    // Native is the moment someone is actually trying to pay. Whatever we are
    // still building, they need to hear "not right now, nothing was charged" —
    // never that the product is unfinished.
    return isNative
      ? t('unlock.unavailable')
      : 'Astrally is purchased through Google Play in the Android app. Your unlock then applies to this account everywhere, including here on the web.';
  }
  return raw.replace(/^(FirebaseError|Error):\s*/g, '').slice(0, 220);
}

/**
 * Base purchase — permanently unlocks the core app for this ACCOUNT.
 *
 * All payments go through Google Play Billing in the Android app; one
 * purchase unlocks the same account on web because entitlements are keyed by
 * uid. So this screen offers a real purchase only on native, and on web
 * offers "I've already purchased" which re-reads the server entitlement.
 */
export function UnlockScreen() {
  const { t } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The price is remote config: there is no honest local fallback for it. A
  // hardcoded one used to render a real-looking ₹49 button that was disabled,
  // so tapping it did nothing — a priced CTA must never quote a guess.
  const cfg = app.entitlement?.config.base;
  const price = cfg?.priceDisplay.inr;
  // Purchases happen through Google Play in the Android app. On web we can
  // only re-read the entitlement that a verified Play purchase produced.
  const isNative = Capacitor.isNativePlatform();

  /**
   * The CTA stays pressable while the price loads, so the tap has to be
   * answered — re-fetching the entitlement is that answer, since the snapshot
   * is the thing that's missing.
   */
  const loadPrice = async () => {
    setBusy(true);
    setError('');
    try {
      if (!(await app.refreshEntitlement())) setError(t('unlock.unavailable'));
    } finally {
      setBusy(false);
    }
  };

  const buy = async (platform?: 'play' | 'mock' | 'dev') => {
    if (!cfg) {
      await loadPrice();
      return;
    }
    setBusy(true);
    setError('');
    try {
      await app.purchase(cfg.productId, platform);
      navigate('/home');
    } catch (e) {
      setError(friendlyBillingError(String(e), isNative, t));
    } finally {
      setBusy(false);
    }
  };

  /** Web path: a Play purchase made in the app unlocks this same account. */
  const refresh = async () => {
    setBusy(true);
    setError('');
    try {
      const snap = await app.refreshEntitlement();
      if (snap && snap.tier !== 'none') navigate('/home');
      else setError('No purchase found on this account yet.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen fade-in" style={{ paddingTop: '8vh' }}>
      <div className="center" style={{ marginBottom: 18 }}>
        <div className="row" style={{ justifyContent: 'center' }}>
          <AstrallyMark size={56} />
        </div>
        <div className="h1">{t('unlock.title')}</div>
        <div className="muted small" style={{ marginTop: 8, lineHeight: 1.55 }}>
          {t('unlock.body')}
        </div>
      </div>

      <div className="card card--strong col" style={{ gap: 6 }}>
        <div className="section-title" style={{ margin: 0 }}>{t('unlock.includes')}</div>
        {[
          'Complete Vedic + Western charts, dashas, transits & placements',
          'Saved birth profiles and daily astrology',
          // The exact allowance comes from config; the vague line is only the
          // placeholder while the snapshot is still loading.
          cfg
            ? t('unlock.limit.ally', {
                n: cfg.aiTrialMessagesPerDay,
                credits: cfg.introductoryCredits,
              })
            : t('unlock.limit.allyPending'),
        ].map((f) => (
          <div key={f} className="small">✓ {f}</div>
        ))}
      </div>

      {/* Connections are a separate, optional layer — never implied to be
          switched on by paying. */}
      <div className="card col" style={{ gap: 6, marginTop: 12 }}>
        <div className="row">
          <span className="chip chip--fact" style={{ cursor: 'default' }}>
            🔒 {t('onb.welcome.optionalBadge')}
          </span>
        </div>
        <div className="h3">If you ever turn connections on</div>
        {[
          // Same rule as the price: quote the allowance only once the config
          // that defines it has arrived.
          cfg
            ? `${cfg.lifetimeOutgoingRequests} lifetime outgoing connection requests`
            : 'A lifetime allowance of outgoing connection requests',
          'Unlimited incoming requests, accepts and human messaging',
        ].map((f) => (
          <div key={f} className="small muted">• {f}</div>
        ))}
        <div className="tiny faint">
          You stay invisible until you switch this on yourself — buying Astrally does not put you
          in front of anyone.
        </div>
      </div>

      <div className="banner banner--gold small" style={{ marginTop: 12 }}>
        {/* Same rule as the price: the number is quoted from config once it
            has arrived, never baked into the string. */}
        {cfg
          ? t('unlock.limit.requests', { n: cfg.lifetimeOutgoingRequests })
          : t('unlock.limit.requestsPending')}
      </div>

      {error && <div className="banner banner--danger small" style={{ marginTop: 10 }}>{error}</div>}

      {isNative ? (
        <button
          className={`btn btn--primary btn--block ${cfg ? '' : 'btn--incomplete'}`}
          style={{ marginTop: 16 }}
          disabled={busy}
          onClick={() => buy()}
        >
          {busy ? (
            <span className="spinner" />
          ) : cfg ? (
            `${t('unlock.cta')} ${price}`
          ) : (
            <>
              <span className="spinner" />
              {t('unlock.pricePending')}
            </>
          )}
        </button>
      ) : (
        <>
          <div className="banner banner--info small" style={{ marginTop: 16, lineHeight: 1.55 }}>
            <b>Unlock in the Astrally Android app{cfg ? ` (${price})` : ''}.</b> Payment goes
            through Google Play, and your unlock applies to this same account here on the web —
            nothing to buy twice.
          </div>
          <button
            className="btn btn--primary btn--block"
            style={{ marginTop: 12 }}
            disabled={busy}
            onClick={refresh}
          >
            {busy ? <span className="spinner" /> : "I've already purchased — refresh"}
          </button>
        </>
      )}

      {DEV_UNLOCK_BUILD && (
        <div className="card" style={{ marginTop: 14, borderStyle: 'dashed' }}>
          <div className="tiny gold" style={{ marginBottom: 6 }}>
            ⚙ Developer build
          </div>
          <div className="tiny muted" style={{ marginBottom: 8, lineHeight: 1.5 }}>
            Unlocks this account without a payment, for testing before Play Billing is
            connected. Works only for emails on the server-side allowlist.
          </div>
          <button
            className="btn btn--ghost btn--small btn--block"
            disabled={busy}
            onClick={() => buy('dev')}
          >
            {busy ? <span className="spinner" /> : 'Developer unlock'}
          </button>
        </div>
      )}
    </div>
  );
}
